<template>
  <Modal
    v-model="visible"
    :closable="closable"
    :mask-closable="maskClosable"
    :width="width"
    class="modal"
    :draggable="draggable"
    @on-visible-change="onCloseDropZone"
    @dragover.prevent.native
    @drop.prevent.native
  >
    <div slot="header">
      <div class="modal-title">
        <p>
          <i class="fas fa-cloud-upload-alt" />
          <span class="description">
            {{ title }}
          </span>
        </p>
      </div>
    </div>

    <p
      v-if="message"
      v-dompurify-html="message"
    />

    <div
      ref="dropZone"
      class="drop-zone-container"
      @click="openFileWindow"
      @drop="handleFiles($event, true)"
      @dragover="dropZoneDragOver"
      @dragleave="dropZoneDragLeave"
    >
      <input
        ref="files"
        type="file"
        name="file"
        multiple
        hidden
        :accept="filesAccepted"
        @change="handleFiles"
      >
      <div
        v-if="!files.length"
        class="placeholder-block"
      >
        <span class="placeholder">
          {{ placeholder }}
        </span>
      </div>
      <div
        v-else
        class="file-row"
      >
        <div
          v-for="(file, index) in files"
          :key="file.name"
          class="file-item"
        >
          <div class="preview">
            <img
              v-if="file.isImage"
              :src="file.src"
              class="preview"
            >
          </div>
          <div class="description">
            {{ file.name }}
          </div>
          <div class="remove">
            <i
              class="fas fa-times"
              @click.stop="removeFile(index)"
            />
          </div>
        </div>
      </div>
    </div>

    <div slot="footer">
      <div class="modal-buttons">
        <Button
          type="success"
          size="large"
          :disabled="isDisabled"
          class="button"
          @click="processFiles"
        >
          {{ buttonLabel }}
        </Button>
        <Button
          type="default"
          size="large"
          class="button"
          @click="closeDropZone"
        >
          Cancel
        </Button>
      </div>
    </div>
  </Modal>
</template>

<script>
export default {
  props: {
    value: {
      type: Boolean,
      default: false
    },
    closable: {
      type: Boolean,
      default: true
    },
    maskClosable: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    message: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Drop files here or click to select'
    },
    attachment: {
      type: Boolean,
      default: false
    },
    width: {
      type: [Number, String],
      default: 520
    },
    draggable: {
      type: Boolean,
      default: false
    },
    buttonLabel: {
      type: String,
      default: 'Upload'
    },
    fileTypes: {
      type: Array,
      default: () => [
        'audio/*',
        'video/*',
        'image/*',
        'application/*'
      ]
    },
    multiple: {
      type: Boolean,
      default: false
    },
    maxFiles: {
      type: Number,
      default: 0
    },
    maxSize: {
      type: Number,
      default: 0
    },
    fileUnit: {
      type: String,
      default: 'b'
    }
  },
  data () {
    return {
      visible: this.value,
      modalData: {},
      files: [],
      filesList: [],
      tempIndex: 1
    }
  },
  computed: {
    filesAccepted () {
      return this.fileTypes.join('|')
    },
    isDisabled () {
      return !this.files.length
    },
    validFileTypes () {
      const validFileTypes = []
      this.fileTypes.forEach(type => {
        const fileType = type.split('/').shift()
        validFileTypes.push(fileType)
      })
      return validFileTypes
    }
  },
  watch: {
    value (isVisible) {
      this.visible = isVisible
    }
  },
  methods: {
    /**
     * Triggers the Cancel action if the modal was closed by the "close" icon or by pressing "ESC" key.
     * @param {Boolean} isVisible
     */
    onCloseDropZone (isVisible = false) {
      if (!isVisible) {
        this.closeDropZone()
      }
    },

    /**
     * Notifices the parent that the modal should be closed.
     */
    closeDropZone () {
      this.files = []
      this.$emit('closeDropZone')
    },

    /**
     * Triggers the Input File click event to open a File Manager.
     */
    openFileWindow () {
      this.$refs.files.click()
    },

    /**
     * Changes the background color to let the user know he/she is over the Drop Zone when
     * he/she is dragging files into it.
     */
    dropZoneDragOver () {
      this.$refs.dropZone.classList.add('hover')
    },

    /**
     * Puts back the background color of the Drop Zone.
     */
    dropZoneDragLeave () {
      this.$refs.dropZone.classList.remove('hover')
    },

    /**
     * Handles the files dropped by the user or when the user clicks on the Drop Zone.
     * @param {Object} e
     * @param {Boolean} isDroppedFile
     */
    handleFiles (e, isDroppedFile = false) {
      const files = !isDroppedFile
        ? this.$refs.files.files
        : e.dataTransfer.files

      // Validate the files.
      this.validateFiles(files)

      // Clear the input file object.
      this.$refs.files.value = null
      this.dropZoneDragLeave()
    },

    /**
     * Validates that every item is a file and if each one commits with the valid types.
     * @param {Object} files
     */
    validateFiles (files) {
      // If there's no files to validate, exit from the function.
      if (!files) {
        return
      }

      // Transform the received files into an array.
      let postFiles = Array.prototype.slice.call(files)

      // If it should handle only one file, take the first one from the list.
      if (!this.multiple) {
        postFiles = postFiles.slice(0, 1)
      }

      // If there's no file to upload, exit the function.
      if (postFiles.length === 0) {
        return
      }

      // If there's an overflow, display a warning message.
      if (this.maxFiles > 0 && postFiles.length >= this.maxFiles) {
        const filesLabel = this.maxFiles === 1 ? 'file' : 'files'
        this.$Notice.warning({
          title: 'Exceding maximum files limit',
          desc: `You cannot upload more than ${this.maxFiles} ${filesLabel}.`
        })
      } else {
        // Validate the Files and the maximum allowed.
        postFiles.forEach(file => {
          // Validate if it is a valid file.
          const isFile = !!file.type.length
          const isFileValid = this.validFileTypes.find(type => {
            return file.type.includes(type) || file.name.includes(type)
          })

          // If the item is a file and it's a valid one, add it into the files array.
          if (isFile && isFileValid) {
            // If the file is an image, create its preview.
            if (file.type.includes('image/')) {
              file.isImage = true
              file.src = URL.createObjectURL(file)
            }

            // Add the file into the "files" array.
            this.files.push(file)
          }
        })
      }
    },

    /**
     * Removes a file from the preview list.
     */
    removeFile (index) {
      this.files.splice(index, 1)
    },

    /**
     * Sends the valid files to the handled by the parent component.
     */
    processFiles () {
      // Set equivalences
      const equivalences = {
        b: 1,
        kb: 1024,
        mb: 1024 * 1024,
        gb: 1024 * 1024 * 1024,
        tb: 1024 * 1024 * 1024 * 1024
      }

      const validFiles = []

      // Fetch only those files which fits with the max size limit (if applies).
      this.files.forEach(file => {
        // If there's a max size limit, validate if the current file doesn't exceed it.
        if (this.maxSize > 0) {
          const maxLimit = this.maxSize * equivalences[this.fileUnit.toLowerCase()]
          if (file.size > maxLimit) {
            this.$Notice.warning({
              title: 'Exceeding file size limit',
              desc: `File ${file.name} is bigger than ${this.maxSize}${this.fileUnit.toUpperCase()}.`
            })
          } else {
            validFiles.push(file)
          }
        } else {
          validFiles.push(file)
        }
      })

      // Store the valid files into the files array.
      this.files = validFiles

      // If the DropZone is for attachments, send the files array to the parent component. Otherwise, upload the files.
      if (this.attachment) {
        this.$emit('onAttachFiles', this.files)
      } else {
        this.uploadImages()
      }

      // Close the Drop Zone.
      this.closeDropZone()
    },

    /**
     * Uploads the existing images and send them to the parent in order to be embedded.
     * TODO: Analize the way to move this out to make this component general and not exclusive for FORUM.
     */
    async uploadImages () {
      if (!this.files.length) {
        return
      }

      this.$emit('onEmbedImages', this.files)
    }
  }
}
</script>

<style scoped>
  .modal >>> .ivu-modal-mask,
  .modal >>> .ivu-modal-wrap {
    z-index: 9999 !important;
  }

  .modal-title {
    padding: 8px 16px;
    border-bottom: 1px solid var(--color-gray-border);
    text-align: center;
  }
  .modal-title .icon,
  .modal-title .description {
    font-size: 16px;
  }
  .modal-content {
    font-size: 14px;
  }
  .modal-buttons {
    padding: 8px 16px;
    border-top: 1px solid var(--color-gray-border);
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
  }
  .modal >>> .ivu-modal-header,
  .modal >>> .ivu-modal-footer {
    margin: 0;
    padding: 0;
    border: none;
  }
  .drop-zone-container {
    margin: 0 auto;
    padding: 8px;
    display: flex;
    align-items: flex-start;
    text-align: justify;
    justify-content: center;
    color: var(--color-mid-gray);
    height: 270px;
    width: 100%;
    border: 2px dashed var(--color-anchor);
    border-radius: 10px;
    cursor: pointer;
    font-weight: 700;
    background-color: var(--color-light-gray);
    opacity: 1;
    overflow-y: auto;
  }
  .drop-zone-container.hover {
    color: var(--color-white);
    background-color: var(--color-anchor-light);
  }
  .placeholder-block {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .file-row {
    width: 100%;
    display: flex;
    flex-direction: column;
  }
  .file-item {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
  }
  .file-item div {
    display: flex;
  }
  .file-item .preview {
    width: 50px;
    height: 50px;
  }
  .file-item .preview img {
    width: 100%;
  }
  .file-item .description {
    padding: 0 8px;
    width: calc(100% - 50px - 16px);
    color: var(--color-black);
  }
  .file-item .remove {
    width: 16px;
    height: 16px;
    color: var(--color-red-border);
    opacity: .5;
  }
  .file-item .remove:hover {
    opacity: 1;
  }
</style>
